import { Divider, Flex, HStack, IconButton, Tooltip } from '@cad-arc';
import { LogOnUnitButton } from '../../log-on-edit-unit/log-on-edit-unit-button';
import { QueueCreationButton } from '../../queues/components/queue-creation-button';
import { CommandLineButton } from '../../command-line/command-line-button';
import { HamburgerMenu } from './hamburger-menu';
import { InitialsMenu } from './initials-menu';
import { ContactListButton } from '../../contact-list/contact-list-button';
import { CallTakerStationMenu } from '../../call-taker-station/call-taker-station-menu';
import { LocationBiasMenu } from '../../location-bias/location-bias-menu';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { useIsDexEnabled } from '../../data-exchange/use-dex-access-guard';
import { WorkspaceManagementMenu } from '../../workspace-management/workspace-management-menu';
import { observer } from 'mobx-react-lite';
import { NavLink } from 'react-router-dom';
import { NewEventButton } from '../../event-panel/new-event-button';
import { DexFormButton } from '../../data-exchange/forms/dex-form-button';
import { DexFormsButton } from '../../data-exchange/forms/dex-forms-button';
import { DexResultsButton } from '../../data-exchange/results/dex-results-button';
import { MessagingButton } from '../../messaging/components/messaging-button';
import { MonitoredEntitiesButton } from '../../panels/entity-panels/monitored-entities/monitored-entities-button';
import { SensorEventsButton } from '../../sensors/sensor-events-button';
import { useSensorsEnabled } from '../../sensors/use-sensors-enabled';
import { ExportsMenu } from '../../exports/components/exports-menu';
import { useWorkspaceWindowType } from '../../common/hooks/use-workspace-window-type';
import {
    EVENTS_WINDOW_PATH,
    EVENT_SEARCH_WINDOW_PATH,
    QUEUES_WINDOW_PATH,
    UNIT_STATUS_SEARCH_WINDOW_PATH,
    MAP_WINDOW_PATH,
    BOLO_SEARCH_WINDOW_PATH,
} from '../../router/constants';
import { usePanelsPermittedInWindow } from '../../common/hooks/use-panels-permitted-in-window';
import { useIsMapWindow } from '../../common/hooks/use-is-map-window';
import { ConnectionStatusIndicator } from './connection-status-indicator';
import { NewBoloButton } from '../../bolos/bolo-panel/new-bolo-button';
import { useApplicationType } from '../../common/hooks/use-application-type';

export const APP_HEADER_HEIGHT_PX = 35;

export const AppHeader = observer(function AppHeader() {
    const { isWeb, isCad } = useApplicationType();
    const enabledFeatures = useEnabledFeatures();
    const { isDexEnabled } = useIsDexEnabled();
    const workspaceWindowType = useWorkspaceWindowType();
    const isQueuesWindow = workspaceWindowType === 'QUEUE_V2';
    const isEventsWindow = workspaceWindowType === 'EVENT_V2';
    const panelsPermittedInWindow = usePanelsPermittedInWindow();
    const sensorsEnabled = useSensorsEnabled();
    const isMapWindow = useIsMapWindow();
    /*
     * The Electron map window keeps its minimal header even once MAP_V2 permits panels (RND-73560):
     * the only panels it hosts are sensor panels, opened from the map itself.
     *
     * Web shows these buttons everywhere; from `/map` they navigate to the queues route and open
     * their panel in the same click, before the store has switched windows. With sensors on,
     * `/map` has its own workspace window, which cannot hold these panels, so they are hidden
     * there. With sensors off `/map` shares the queues window as it always has.
     */
    const showWorkspacePanelButtons =
        (panelsPermittedInWindow && !isMapWindow) || (isWeb && !(isMapWindow && sensorsEnabled));
    const showLocationBiasMenu = enabledFeatures.filterLocationByDispatchAreaEnabled && isCad;
    return (
        <Flex
            h={`${APP_HEADER_HEIGHT_PX}px`}
            w="100%"
            alignItems="center"
            backgroundColor="surface.foreground"
            justifyContent="space-between"
            borderBottomWidth="1px"
            borderStyle="solid"
            borderColor="border.default"
        >
            <HStack ml={2}>
                <HamburgerMenu
                    trigger={
                        <IconButton
                            icon="Menu"
                            aria-label="hamburger menu"
                            variant="ghost"
                            size="lg"
                        />
                    }
                />
                {isWeb ? (
                    <>
                        <NavLink to={QUEUES_WINDOW_PATH}>
                            {({ isActive }) => (
                                <Tooltip content="Queues">
                                    <IconButton
                                        isActive={isActive}
                                        icon="Queues"
                                        aria-label="navigate to queues"
                                        variant="ghost"
                                    />
                                </Tooltip>
                            )}
                        </NavLink>
                        <NavLink to={EVENTS_WINDOW_PATH}>
                            {({ isActive }) => (
                                <Tooltip content="Events">
                                    <IconButton
                                        isActive={isActive}
                                        icon="CADEvent"
                                        aria-label="navigate to events"
                                        variant="ghost"
                                    />
                                </Tooltip>
                            )}
                        </NavLink>
                        {enabledFeatures.disableLegacyMapAndSearches ||
                        enabledFeatures.mapWindowEnabled ? (
                            <NavLink to={MAP_WINDOW_PATH}>
                                {({ isActive }) => (
                                    <Tooltip content="Map">
                                        <IconButton
                                            isActive={isActive}
                                            icon="Map"
                                            aria-label="navigate to map"
                                            variant="ghost"
                                        />
                                    </Tooltip>
                                )}
                            </NavLink>
                        ) : (
                            <Tooltip content="Map">
                                <IconButton
                                    icon="Map"
                                    aria-label="open legacy map in new tab"
                                    variant="ghost"
                                    onClick={() => {
                                        window.open('/cad/#/map', '_blank');
                                    }}
                                />
                            </Tooltip>
                        )}
                        {enabledFeatures.disableLegacyMapAndSearches ||
                        enabledFeatures.searchWindowsEnabled ? (
                            <NavLink to={EVENT_SEARCH_WINDOW_PATH}>
                                {({ isActive }) => (
                                    <Tooltip content="Event Search">
                                        <IconButton
                                            isActive={isActive}
                                            icon="SearchEvents"
                                            aria-label="navigate to event search"
                                            variant="ghost"
                                        />
                                    </Tooltip>
                                )}
                            </NavLink>
                        ) : undefined}
                        {enabledFeatures.searchBolos ? (
                            <NavLink to={BOLO_SEARCH_WINDOW_PATH}>
                                {({ isActive }) => (
                                    <Tooltip content="BOLO Search">
                                        <IconButton
                                            isActive={isActive}
                                            icon="SearchBolo"
                                            aria-label="navigate to bolo search"
                                            variant="ghost"
                                        />
                                    </Tooltip>
                                )}
                            </NavLink>
                        ) : undefined}
                        {enabledFeatures.disableLegacyMapAndSearches ||
                        enabledFeatures.unitStatusSearchWindowEnabled ? (
                            <NavLink to={UNIT_STATUS_SEARCH_WINDOW_PATH}>
                                {({ isActive }) => (
                                    <Tooltip content="Unit Status Search">
                                        <IconButton
                                            isActive={isActive}
                                            icon="SearchUnitStatuses"
                                            aria-label="navigate to unit status search"
                                            variant="ghost"
                                        />
                                    </Tooltip>
                                )}
                            </NavLink>
                        ) : undefined}
                        {showWorkspacePanelButtons ? (
                            <Divider
                                orientation="vertical"
                                color="border.default"
                                style={{ height: '25px' }}
                            />
                        ) : undefined}
                    </>
                ) : undefined}
                {showWorkspacePanelButtons ? (
                    <>
                        <CommandLineButton />
                        {enabledFeatures.messaging ? <MessagingButton /> : undefined}
                        {enabledFeatures.viewContactList ? <ContactListButton /> : undefined}
                        {isDexEnabled ? (
                            enabledFeatures.dexNextEnabled ? (
                                <DexFormButton />
                            ) : (
                                <DexFormsButton />
                            )
                        ) : undefined}
                        {isDexEnabled ? <DexResultsButton /> : undefined}
                    </>
                ) : undefined}
                {isQueuesWindow ? (
                    <>
                        <QueueCreationButton />
                        <LogOnUnitButton />
                    </>
                ) : undefined}
                {isEventsWindow ? <MonitoredEntitiesButton /> : undefined}
                {/* The map window only: that is where the sensor panels it opens live, and where the
                    alert toasts appear. */}
                {sensorsEnabled && isMapWindow ? <SensorEventsButton /> : undefined}
                {isEventsWindow ? <NewEventButton /> : undefined}
                {isEventsWindow && enabledFeatures.createBolos ? <NewBoloButton /> : undefined}
            </HStack>
            <HStack mr={2}>
                <ExportsMenu />
                <CallTakerStationMenu />
                {showLocationBiasMenu && <LocationBiasMenu />}
                <WorkspaceManagementMenu />
                <InitialsMenu />
                <ConnectionStatusIndicator />
            </HStack>
        </Flex>
    );
});
